import React, { useState, useRef, useEffect, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

interface Option {
  Id: string | number;
  Name: string;
}

interface SelectFieldProps {
  name: string;
  label: string;
  isAsterisk?: boolean;
  options: Option[];
  required?: boolean;
  requiredMessage?: string;
  placeholderText?: string;
  value?: string | number;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;

  /** NEW PROP */
  isSearchable?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  isAsterisk = true,
  required = false,
  options,
  requiredMessage = "Please select an option",
  placeholderText = "Select an option",
  value,
  className = "",
  onChange,
  disabled,

  /** NEW PROP */
  isSearchable,
}) => {
  // For testing
  // options = options.slice(0, 6);

  // if options length is greater than 7 then make isSearchable true
  isSearchable = false;
  if (options.length > 7) {
    isSearchable = true;
  }

  const {
    control,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Autofocus search box ONLY when searchable
  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isSearchable]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply filtering ONLY when isSearchable = true
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) => opt.Name.toLowerCase().includes(term));
  }, [options, searchTerm, isSearchable]);

  const getSelectedLabel = (selectedValue: string | number) => {
    const selected = options.find(
      (opt) => opt.Id.toString() === selectedValue?.toString(),
    );
    return selected ? selected.Name : placeholderText;
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {isAsterisk && <span className="text-danger">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={{ required: required ? requiredMessage : false }}
        render={({ field }) => {
          const selectedValue = value ?? field.value;
          const showErrorMessage =
            (isSubmitted || errors[name]?.type === "maxLength") && errors[name];

          return (
            <div className="custom-select-container" ref={dropdownRef}>
              {/* Trigger */}
              <div
                className={`custom-select-trigger form-control 
    ${isOpen ? "open" : ""} 
    ${(() => {
      const parts = name.split(".");
      if (parts.length === 3 && parts[0] === "payments") {
        const index = parseInt(parts[1], 10);
        if (!isNaN(index)) {
          return errors.payments?.[index]?.PaymentType &&
            (isSubmitted ||
              errors.payments?.[index]?.PaymentType?.type === "maxLength")
            ? "is-invalid"
            : "";
        }
      }
      return showErrorMessage ? "is-invalid" : "";
    })()} 
    ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
              >
                <span
                  className={field.value ? "selected" : "placeholder"}
                  style={{ paddingRight: "25px" }}
                >
                  {getSelectedLabel(selectedValue)}
                </span>

                <div className={`select-arrow ${isOpen ? "rotate" : ""}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Dropdown */}
              {isOpen && !disabled && (
                <div className="custom-select-dropdown">
                  {/* Search bar (ONLY when isSearchable = true) */}
                  {isSearchable && (
                    <div
                      className="custom-select-search"
                      style={{ padding: "8px" }}
                    >
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="custom-select-search-input"
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          border: "1px solid var(--Grey-Subtext2)",
                          borderRadius: "6px",
                          backgroundColor: "var(--bg-secondary)",
                          color: "var(--Grey-Heading)",
                        }}
                      />
                    </div>
                  )}

                  <div className="custom-select-options">
                    {filteredOptions.length === 0 ? (
                      <div className="custom-select-option disabled">
                        No options found
                      </div>
                    ) : (
                      filteredOptions.map((option, index) => (
                        <React.Fragment key={option.Id}>
                          <div
                            className={`custom-select-option ${
                              selectedValue?.toString() === option.Id.toString()
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => {
                              field.onChange(option.Id);

                              if (onChange) {
                                const syntheticEvent = {
                                  target: { value: option.Id.toString(), name },
                                } as React.ChangeEvent<HTMLSelectElement>;
                                onChange(syntheticEvent);
                              }

                              setIsOpen(false);
                              setSearchTerm("");
                            }}
                          >
                            {option.Name}
                          </div>

                          {index < filteredOptions.length - 1 && (
                            <div className="option-divider" />
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Hidden native select */}
              <select
                {...field}
                value={selectedValue ?? ""}
                onChange={(e) => {
                  field.onChange(e);
                  onChange?.(e);
                }}
                disabled={disabled}
                style={{ display: "none" }}
              >
                <option value="">{placeholderText}</option>
                {options.map((option) => (
                  <option key={option.Id} value={option.Id}>
                    {option.Name}
                  </option>
                ))}
              </select>
            </div>
          );
        }}
      />

      {/* Dropdown ke bahar – bilkul pehle jaisi jagah pe */}
      {(() => {
        const parts = name.split(".");
        if (parts.length === 3 && parts[0] === "payments") {
          const index = parseInt(parts[1], 10);
          if (!isNaN(index)) {
            const fieldError = errors.payments?.[index]?.PaymentType;
            if (fieldError && isSubmitted) {
              return (
                <div className="invalid-feedback d-block">
                  {fieldError.message}
                </div>
              );
            }
          }
        }
        // normal field ke liye
        // Using Controller's fieldState is better, but since this is outside, we use errors + isSubmitted
        // (isTouched is only available inside render, so we rely on isSubmitted for external error display)
        return errors[name] && isSubmitted ? (
          <div className="invalid-feedback d-block">
            {errors[name]?.message}
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default SelectField;
